/**
 * @fileoverview Input/output schemas for inspiration-ideas-generator-v2.
 *
 * This agent generates simple plot ideas (title + summary) inspired by existing stories.
 * The selected idea is then expanded by short-story-planner-v4 or the
 * story-planner-workflow-v1 workflow (use-long-story-planner.ts).
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// HOOK INPUT SCHEMA
// =============================================================================

export const HookInputSchema = z.object({
  existingStories: z.array(z.string()).describe('Array of story contents to use as inspiration.'),
  userMessage: z.string().describe('User instructions for inspiration-based ideation.'),
});

export type HookInput = z.infer<typeof HookInputSchema>;

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema
  .merge(AGENT_SCHEMA_OBJECTS.storyContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.loreContext.schema)
  .merge(AGENT_SCHEMA_OBJECTS.existingStories.schema)
  .merge(AGENT_SCHEMA_OBJECTS.userMessage.schema);

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  ideas: z.array(z.object({
    title: z.string().describe('A compelling, evocative title for this inspired story idea.'),
    summary: z.string().describe('A concise, 100-word summary of the concept.'),
  })).length(3).describe('An array of exactly three distinct story ideas inspired by the source material.'),
});

export type Output = z.infer<typeof OutputSchema>;
